import React from 'react'
import { VerifiedRounded, Lock } from '@mui/icons-material'
import cn from '../../../../../lib/utils/cn'
import Button from '../../../../ui/Button'
import { CurrentUserObject } from '../../../../../lib/api/types/user.interfaces'
import { Plan } from '../../../../../lib/api/types/billing.interfaces'
import type { RegionalPlanProduct } from '../../../../../lib/api/types/pricing.interfaces'
import PlanPrice from './PlanPrice'
import PlanUsersControl from './PlanUsersControl'
import PlanFeatures from './PlanFeatures'
import { isCurrentPurchasedPlan, getPriceIdForPeriod } from '../utils'
import { PaymentPeriodType } from '../../../../../lib/api/types/billing.interfaces'

interface PlanCardProps {
	plan: RegionalPlanProduct
	isActive: boolean
	currentPlan?: Plan
	currentUser?: CurrentUserObject
	period: PaymentPeriodType
	onPlanChange: (planName: string) => void
	onClose: () => void
	onConfigurePlan?: () => void
}

const PlanCard: React.FC<PlanCardProps> = ({
	plan,
	isActive,
	currentPlan,
	currentUser,
	period,
	onPlanChange,
	// onClose,
	// onConfigurePlan,
}) => {
	const selectedPriceId = getPriceIdForPeriod(plan.prices, period)
	const isCurrentPurchased = isCurrentPurchasedPlan(
		plan.name,
		period,
		currentPlan,
		selectedPriceId,
		currentUser?.plan?.priceId,
	)

	const isLocked =
		isCurrentPurchased && plan?.name === 'Free'
			? false // Free plan is never locked if it's CURRENT plan - but user will not be able to downgrade from org-plans to free
			: (currentUser?.organization?.id
					? ['Free', 'Pro']
					: ['Enterprise', 'Business']
				)?.includes(plan?.name)

	return (
		<div
			className={cn(
				'border rounded-lg w-full lg:w-1/4 lg:min-w-[300px] shadow-xl relative h-[600px] box-border flex flex-col',
				isActive
					? 'border-accent bg-accent-light dark:bg-background-dark-hover'
					: 'border-stroke-dark dark:border-stroke-light-dark bg-white bg-transparent',
				!isActive && isLocked && 'dark:border-stroke-light-dark/30',
				isLocked && 'select-none',
			)}
		>
			{isCurrentPurchased && (
				<div
					className={cn(
						'absolute z-20 -top-3.5 left-1/2 -translate-x-1/2 w-fit rounded-full borders flex items-center gap-1 justify-center bg-accent py-1 px-1.5',
					)}
				>
					<VerifiedRounded className="fill-white w-5 h-5" />
					<span className=" text-white text-base pr-1">Current</span>
				</div>
			)}
			<div
				className={cn(
					'flex flex-col items-start gap-2 border-b justify-center font-extrabold text-text-main dark:text-text-main-dark text-xl h-24 min-h-24 px-5 box-border',
					isActive
						? 'border-accent'
						: 'border-stroke-dark dark:border-stroke-light-dark',
					!isActive && isLocked && 'dark:border-stroke-light-dark',
				)}
			>
				<p className="text-xl">{plan?.name}</p>
				<PlanPrice plan={plan} isActive={isActive} />
			</div>

			{isLocked && (
				<div className="absolute inset-0 bg-background-locker-light dark:bg-background-locker-dark flex items-center justify-center z-10 pointer-events-none gap-2 rounded-md">
					<div className="flex flex-col items-center justify-center bg-white drop-shadow-xl p-3 rounded-md gap-2.5 w-4/5 border border-accent">
						<Lock className="w-11 h-14 fill-accent" />
						<p className="text-xl !font-sans text-center flex items-center tracking-normal justify-center flex-col">
							Only for{' '}
							<span className="text-accent !font-sans font-bold  tracking-wider">
								{currentUser?.organization?.id ? 'Personal' : 'Organization'}{' '}
								Account
							</span>
						</p>
					</div>
				</div>
			)}

			<div className="p-5 flex flex-col gap-2.5">
				<PlanUsersControl
					planName={plan?.name}
					isActive={isActive}
					currentPlan={currentPlan}
					currentUser={currentUser}
					period={period}
					selectedPriceId={selectedPriceId}
					canSelect={!isLocked}
					onPlanChange={onPlanChange}
				/>
				<PlanFeatures features={plan?.marketingFeatures} isActive={isActive} />
			</div>

			<div className="px-5 flex flex-col items-center justify-center mt-auto pb-5 gap-2">
				{/* TODO: temp fix - hide Configure plan; blocked by API quotas issue; re-enable when fixed		*/}
				{/* {isCurrentPurchased && currentPlan?.name !== 'Free' && !currentPlan?.trial ? (
					<Button
						variant={isActive ? 'primary' : 'secondary'}
						label="Configure plan"
						className="w-full"
						disabled={!!currentPlan?.trial}
						tooltipOnDisabled
						tooltip={onConfigurePlan && !!currentPlan?.trial && "Trial plans can’t be edited."}
						onClick={() => {
							if (!!onConfigurePlan) {
								onClose()
								onConfigurePlan()
							}
						}}
					/>
				) : ... } */}
				{isCurrentPurchased && !isActive ? (
					<Button
						variant="secondary"
						label="Choose Plan"
						className="w-full"
						onClick={() => {
							!isLocked && onPlanChange(plan?.name)
						}}
					/>
				) : (
					<Button
						variant={isActive ? 'primary' : 'secondary'}
						label={isActive ? 'Selected Plan' : 'Choose Plan'}
						className="w-full"
						disabled={isActive}
						onClick={() => {
							!isActive && !isLocked && onPlanChange(plan?.name)
						}}
					/>
				)}
				{plan.description && (
					<p className="text-xs text-text-secondary dark:text-text-table-dark text-center leading-snug px-1">
						{plan.description}
					</p>
				)}
			</div>
		</div>
	)
}

export default PlanCard
